import {
  Box,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { lazy, Suspense, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  MdSportsTennis,
  MdEvent,
  MdPeople,
  MdBarChart,
  MdAdd,
  MdCalendarMonth,
  MdCardMembership,
  MdLocalOffer,
} from 'react-icons/md';
import PageLayout from '../../components/page-layout';
import Card from '../../components/card/Card';
import { StaggeredGrid, StaggeredItem } from '../../components/animations/StaggeredGrid';
import { spacing, typography, useSemanticColor } from 'theme/tokens';
import { translate } from '../../i18n';
import { useAuth } from '../auth/AuthProvider';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useAppSettings } from '../../contexts/AppSettingsContext';
import WalkthroughChecklist from './components/WalkthroughChecklist';
import ClubhouseDashboard from './components/ClubhouseDashboard';
import { useShowsOnboardingGuides } from './onboarding-guides-audience';

// Analytics components (reused from analytics feature)
import UserCountCard from '../analytics/components/UserCountCard';
import GameCountCard from '../analytics/components/GameCountCard';
import RevenueCard from '../analytics/components/RevenueCard';
import AllCourtsUsageAsPercentageCard from '../analytics/components/AllCourtsUsageAsPercentageCard';
import CumulativeBookingsChart from '../analytics/components/CumulativeBookingsChart';
import CumulativeRevenueChart from '../analytics/components/CumulativeRevenueChart';
import HomepageImpactWidget from '../impact/components/HomepageImpactWidget';

// Entry points to the onboarding video and guided tour, in every environment
// since 2026-11-03; who sees them is `useShowsOnboardingGuides` (same check as
// the launcher in the admin layout). Lazy, so their chunk loads only where shown.
const HomeGuideCards =
  process.env.NODE_ENV !== 'test'
    ? lazy(() => import('../onboarding-lab/HomeGuideCards'))
    : null;

const QUICK_ACTIONS = [
  { icon: MdSportsTennis, label: 'tabs.courts', route: '/admin/courts', color: 'brand.500', iconBg: 'brand.50' },
  { icon: MdEvent, label: 'tabs.events', route: '/admin/events', color: 'success.500', iconBg: 'success.50' },
  { icon: MdPeople, label: 'tabs.users', route: '/admin/users', color: 'info.500', iconBg: 'info.50' },
  { icon: MdBarChart, label: 'tabs.analytics', route: '/admin/analytics', color: 'warning.500', iconBg: 'warning.50' },
  { icon: MdCalendarMonth, label: 'tabs.calendar', route: '/admin/calendar', color: 'accent.600', iconBg: 'accent.50' },
  { icon: MdCardMembership, label: 'tabs.memberships', route: '/admin/memberships', color: 'brand.400', iconBg: 'brand.50' },
  { icon: MdLocalOffer, label: 'coupon.coupons', route: '/admin/coupons', color: 'danger.500', iconBg: 'danger.50' },
  { icon: MdAdd, label: 'tabs.games', route: '/admin/latest-booked-games', color: 'navy.600', iconBg: 'secondaryGray.300' },
];

function formatDate(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export default function HomePage() {
  const { currentUser } = useAuth();
  const { aesthetic } = useAppSettings();
  const uid = currentUser?.sub as string | undefined;
  const selectedOrganization = useSelectedOrganization.use.selectedOrganization();
  const { selectedLocation } = useSelectedLocation();
  const showGuides = useShowsOnboardingGuides();
  const textColor = useSemanticColor('textPrimary');
  const mutedColor = useSemanticColor('textSecondary');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const actionHoverBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const actionBorderColor = useColorModeValue('rgba(0, 0, 0, 0.04)', 'rgba(255, 255, 255, 0.06)');

  const firstName =
    (currentUser?.name as string)?.split(' ')[0] ??
    (currentUser?.email as string)?.split('@')[0] ??
    '';

  // Last 30 days for KPI cards
  const { startDate, endDate, previousStartDate, previousEndDate } = useMemo(() => {
    const now = new Date();
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    const start = new Date(now.getTime() - thirtyDaysMs);
    const prevEnd = new Date(start.getTime());
    const prevStart = new Date(start.getTime() - thirtyDaysMs);
    return { startDate: start, endDate: end, previousStartDate: prevStart, previousEndDate: prevEnd };
  }, []);

  const growthLabel = translate('analytics.vs_previous_30_days' as any);
  const hasFullContext = Boolean(selectedOrganization && selectedLocation);

  // Clubhouse aesthetic: render the scoreboard dashboard instead of classic layout
  if (aesthetic === 'clubhouse') {
    return (
      <PageLayout>
        {HomeGuideCards && showGuides && (
          <Suspense fallback={null}>
            <HomeGuideCards />
          </Suspense>
        )}
        <ClubhouseDashboard />
        {uid && <WalkthroughChecklist uid={uid} />}
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Welcome Header */}
      <Flex justify="space-between" align="flex-end" flexWrap="wrap" gap={2}>
        <Box>
          <Text color={mutedColor} {...typography.caption} mb={1}>
            {formatDate()}
          </Text>
          <Heading as="h1" color={textColor} {...typography.pageTitle} fontSize="3xl">
            {translate('walkthrough.welcome')}
            {firstName ? `, ${firstName}` : ''}
          </Heading>
          {selectedOrganization && (
            <Text color={mutedColor} {...typography.body} mt={1}>
              {selectedOrganization.name}
            </Text>
          )}
        </Box>
      </Flex>

      {HomeGuideCards && showGuides && (
        <Suspense fallback={null}>
          <HomeGuideCards />
        </Suspense>
      )}

      {/* KPI Cards — only when location is selected */}
      {hasFullContext && (
        <StaggeredGrid staggerDelay={0.06}>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap="20px">
            <StaggeredItem>
              <UserCountCard
                boxColor={boxBg}
                brandColor={brandColor}
                startDate={startDate}
                endDate={endDate}
                previousStartDate={previousStartDate}
                previousEndDate={previousEndDate}
                growthLabel={growthLabel}
              />
            </StaggeredItem>
            <StaggeredItem>
              <GameCountCard
                boxColor={boxBg}
                brandColor={brandColor}
                startDate={startDate}
                endDate={endDate}
                previousStartDate={previousStartDate}
                previousEndDate={previousEndDate}
                growthLabel={growthLabel}
              />
            </StaggeredItem>
            <StaggeredItem>
              <RevenueCard
                boxColor={boxBg}
                brandColor={brandColor}
                startDate={startDate}
                endDate={endDate}
                previousStartDate={previousStartDate}
                previousEndDate={previousEndDate}
                growthLabel={growthLabel}
              />
            </StaggeredItem>
            <StaggeredItem>
              <AllCourtsUsageAsPercentageCard
                boxColor={boxBg}
                brandColor={brandColor}
                startDate={startDate}
                endDate={endDate}
                previousStartDate={previousStartDate}
                previousEndDate={previousEndDate}
                growthLabel={growthLabel}
              />
            </StaggeredItem>
          </SimpleGrid>
        </StaggeredGrid>
      )}

      {/* Impact widget — counterfactual metrics */}
      {hasFullContext && (
        <HomepageImpactWidget startDate={startDate} endDate={endDate} />
      )}

      {/* Charts — only when location is selected */}
      {hasFullContext && (
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
          <CumulativeBookingsChart />
          <CumulativeRevenueChart />
        </SimpleGrid>
      )}

      {/* Quick Actions */}
      {hasFullContext && (
        <Box>
          <Text
            color={mutedColor}
            {...typography.formSectionLabel}
            mb={3}
            px={1}
          >
            {translate('walkthrough.quick_actions' as any) || 'Quick Actions'}
          </Text>
          <SimpleGrid columns={{ base: 2, sm: 4, lg: 8 }} gap="12px">
            {QUICK_ACTIONS.map((action) => (
              <NavLink key={action.route} to={action.route}>
                <Card
                  py="16px"
                  px="12px"
                  alignItems="center"
                  cursor="pointer"
                  transition="all 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)"
                  borderColor={actionBorderColor}
                  _hover={{
                    bg: actionHoverBg,
                    transform: 'translateY(-3px)',
                    boxShadow: 'md',
                  }}
                >
                  <Flex
                    w="44px"
                    h="44px"
                    borderRadius="12px"
                    bg={action.iconBg}
                    align="center"
                    justify="center"
                    mb="10px"
                  >
                    <Icon as={action.icon} w="22px" h="22px" color={action.color} />
                  </Flex>
                  <Text
                    {...typography.caption}
                    fontWeight="500"
                    color={textColor}
                    textAlign="center"
                    noOfLines={1}
                  >
                    {translate(action.label as any)}
                  </Text>
                </Card>
              </NavLink>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Walkthrough Checklist */}
      {uid && <WalkthroughChecklist uid={uid} />}
    </PageLayout>
  );
}
